import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LayoutDashboard, FileText, CreditCard, Plus, BookOpen, FileCheck2, Eye, Pencil, Trash2, AlertTriangle, Users } from "lucide-react";
import { toast } from "sonner";

const LANGS = ["English", "Hindi", "Tamil", "Telugu", "Kannada", "Malayalam", "Marathi", "Bengali", "Gujarati", "Punjabi", "Urdu", "Odia"];
const MEDIUMS = ["Kannada", "English", "Marathi"];
const BOARDS = [
  { value: "KSEEB", label: "KSEEB (Karnataka)" },
  { value: "MSBSHSE", label: "MSBSHSE (Maharashtra)" },
  { value: "CBSE", label: "CBSE" },
];
const GRADES = ["10th", "12th"];

const NAV = [
  { key: "home", label: "Overview", to: "/admin", icon: LayoutDashboard },
  { key: "users", label: "Users & Papers", to: "/admin/users", icon: Users },
  { key: "plans", label: "Purchase Plans", to: "/admin/plans", icon: CreditCard },
  { key: "papers", label: "Question Papers", to: "/admin/papers", icon: FileText },
  { key: "failed", label: "Failed evaluations", to: "/admin/failed-evaluations", icon: AlertTriangle },
];

const EMPTY = { title: "", subject: "", language: "English", medium: "English", board: "", grade: "", year: "", total_marks: 0, price_inr: 99, solution_slug: "" };

export default function QuestionPapers() {
  const [papers, setPapers] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [meta, setMeta] = useState(EMPTY);
  const [qpFile, setQpFile] = useState(null);
  const [schemeFile, setSchemeFile] = useState(null);

  const load = () => api.get("/question-papers").then((r) => setPapers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const reset = () => { setEditingId(null); setMeta(EMPTY); setQpFile(null); setSchemeFile(null); };
  const openCreate = () => { reset(); setOpen(true); };
  const openEdit = (p) => {
    setEditingId(p.id);
    setMeta({ title: p.title, subject: p.subject, language: p.language || "English", medium: p.medium || "English", board: p.board || "", grade: p.grade || "", year: p.year || "", total_marks: p.total_marks || 0, price_inr: p.price_inr || 99, solution_slug: p.solution_slug || "" });
    setQpFile(null); setSchemeFile(null);
    setOpen(true);
  };

  const openPdf = async (url) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not open PDF");
    }
  };

  const submit = async () => {
    if (!meta.board) return toast.error("Select the board for this question paper");
    if (!meta.grade) return toast.error("Select the grade for this question paper");
    if (!editingId && (!qpFile || !schemeFile)) return toast.error("Upload both the question paper and scheme PDFs");
    if (qpFile && qpFile.type !== "application/pdf") return toast.error("Question paper must be a PDF");
    if (schemeFile && schemeFile.type !== "application/pdf") return toast.error("Scheme must be a PDF");
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(meta).forEach(([k, v]) => { if (k !== "year" || v !== "") fd.append(k, v); });
      if (qpFile) fd.append("question_paper_file", qpFile);
      if (schemeFile) fd.append("scheme_file", schemeFile);
      if (editingId) {
        await api.put(`/question-papers/${editingId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Question paper updated");
      } else {
        await api.post("/question-papers", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Question paper created");
      }
      setOpen(false); reset(); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/question-papers/${p.id}`);
      toast.success("Question paper deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <DashboardShell nav={NAV} title="Question Papers">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <p className="text-sm text-muted-foreground max-w-xl">Upload the <b>question paper</b> and the <b>scheme of evaluation (with model answers)</b> as PDF files. The AI grades student scripts against them.</p>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
          <Button className="rounded-full gap-2" data-testid="new-paper-button" onClick={openCreate}><Plus className="h-4 w-4" /> New Paper</Button>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit Question Paper" : "Create Question Paper"}</DialogTitle>
              <DialogDescription>{editingId ? "Update details. Leave a PDF empty to keep the existing file." : "Provide details and upload two PDFs."}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Title</Label><Input data-testid="qp-title" value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} /></div>
                <div className="space-y-2"><Label>Subject</Label><Input data-testid="qp-subject" value={meta.subject} onChange={(e) => setMeta({ ...meta, subject: e.target.value })} /></div>
                <div className="space-y-2">
                  <Label>Language (of the paper)</Label>
                  <Select value={meta.language} onValueChange={(v) => setMeta({ ...meta, language: v })}>
                    <SelectTrigger data-testid="qp-language"><SelectValue /></SelectTrigger>
                    <SelectContent>{LANGS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Medium</Label>
                  <Select value={meta.medium} onValueChange={(v) => setMeta({ ...meta, medium: v })}>
                    <SelectTrigger data-testid="qp-medium"><SelectValue placeholder="Select medium" /></SelectTrigger>
                    <SelectContent>{MEDIUMS.map((medium) => <SelectItem key={medium} value={medium}>{medium}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Only students registered with this medium will see the paper.</p>
                </div>
                <div className="space-y-2">
                  <Label>Board</Label>
                  <Select value={meta.board} onValueChange={(v) => setMeta({ ...meta, board: v })}>
                    <SelectTrigger data-testid="qp-board"><SelectValue placeholder="Select board" /></SelectTrigger>
                    <SelectContent>{BOARDS.map(({ value, label }) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Grade / Class</Label>
                  <Select value={meta.grade} onValueChange={(v) => setMeta({ ...meta, grade: v })}>
                    <SelectTrigger data-testid="qp-grade"><SelectValue placeholder="Select grade" /></SelectTrigger>
                    <SelectContent>{GRADES.map((grade) => <SelectItem key={grade} value={grade}>{grade}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Model paper year (optional)</Label><Input type="number" min="1900" max="2100" placeholder="e.g. 2026" value={meta.year} onChange={(e) => setMeta({ ...meta, year: e.target.value })} /></div>
                <div className="space-y-2"><Label>Total marks</Label><Input type="number" data-testid="qp-total-marks" value={meta.total_marks} onChange={(e) => setMeta({ ...meta, total_marks: Number(e.target.value) })} /></div>
                <div className="space-y-2"><Label>Price (₹)</Label><Input type="number" min="1" data-testid="qp-price" value={meta.price_inr} onChange={(e) => setMeta({ ...meta, price_inr: Number(e.target.value) })} /></div>
              </div>

              <div className="space-y-2">
                <Label>Question Paper (PDF){editingId && <span className="text-muted-foreground"> — optional</span>}</Label>
                <Input type="file" accept="application/pdf" data-testid="qp-file-input" onChange={(e) => setQpFile(e.target.files?.[0] || null)} />
                {qpFile && <p className="text-xs text-muted-foreground">{qpFile.name}</p>}
              </div>
              <div className="space-y-2">
                <Label>Scheme of Evaluation + Model Answers (PDF){editingId && <span className="text-muted-foreground"> — optional</span>}</Label>
                <Input type="file" accept="application/pdf" data-testid="scheme-file-input" onChange={(e) => setSchemeFile(e.target.files?.[0] || null)} />
                {schemeFile && <p className="text-xs text-muted-foreground">{schemeFile.name}</p>}
              </div>
              <div className="space-y-2">
                <Label>Solution Slug<span className="text-muted-foreground"> — optional</span></Label>
                <Input data-testid="qp-solution-slug" placeholder="e.g. dsa-graphs" value={meta.solution_slug} onChange={(e) => setMeta({ ...meta, solution_slug: e.target.value })} />
                <p className="text-xs text-muted-foreground">Folder name under <code>/PaperSolution/</code> containing the interactive Thetamorph solution book.</p>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit} disabled={saving} className="rounded-full" data-testid="save-paper-button">{saving ? "Saving…" : editingId ? "Save changes" : "Create paper"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {papers.length === 0 && <p className="text-sm text-muted-foreground">No papers yet. Create one to get started.</p>}
        {papers.map((p) => (
          <div key={p.id} data-testid={`qp-card-${p.id}`} className="bg-background border border-border rounded-lg p-5 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-200">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2 text-primary mb-2"><BookOpen className="h-4 w-4" /><span className="overline text-muted-foreground">{p.subject}</span></div>
              <div className="flex items-center gap-2">
                <button onClick={() => openEdit(p)} className="text-muted-foreground hover:text-primary" data-testid={`edit-qp-${p.id}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(p)} className="text-destructive" data-testid={`delete-qp-${p.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <h3 className="font-heading font-bold text-lg">{p.title}</h3>
            <p className="text-sm text-muted-foreground mt-1">{p.board || "All boards"} · {p.language} · {p.grade || "—"} · {p.year || "Year not set"} · {p.total_marks} marks</p>
            {p.solution_only ? <p className="text-xs text-muted-foreground mt-2">Solution demo · unavailable for exam purchase</p> : <p className="font-heading font-bold text-lg mt-2">₹{p.price_inr || 99}</p>}
            <p className="text-xs text-muted-foreground mt-1">Medium: <b>{p.medium || "English"}</b></p>
            <div className="flex flex-wrap gap-2 mt-4">
              <Button size="sm" variant="outline" className="gap-1.5 rounded-full" asChild><Link to={`/admin/papers/${p.id}/solution`}><BookOpen className="h-3.5 w-3.5" /> Interactive solution</Link></Button>
              {p.question_paper_pdf && (
                <Button size="sm" variant="outline" className="gap-1.5 rounded-full" onClick={() => openPdf(`/question-papers/${p.id}/question-pdf`)} data-testid={`view-qp-${p.id}`}>
                  <Eye className="h-3.5 w-3.5" /> Question paper
                </Button>
              )}
              {p.scheme_pdf && (
                <Button size="sm" variant="outline" className="gap-1.5 rounded-full" onClick={() => openPdf(`/question-papers/${p.id}/scheme-pdf`)} data-testid={`view-scheme-${p.id}`}>
                  <FileCheck2 className="h-3.5 w-3.5" /> Scheme
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </DashboardShell>
  );
}
